import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AudioControls } from "../../audio/AudioControls";
import { audioEngine } from "../../audio/audioEngine";
import { useBattleAudio } from "../hooks/useBattleAudio";
import { useBattleStore } from "../store/battleStore";
import type {
  AnswerValue,
  BattleConfiguration,
  BattleQuestion,
  ServerAnswerResolution
} from "../store/battleStore";
import type { BattleReward } from "../data/battleApi";
import { ArenaBackdrop } from "./ArenaBackdrop";
import { BattleActors } from "./BattleActors";
import type { BattleOpponentVisual } from "./BattleActors";
import { BattleExitDialog } from "./BattleExitDialog";
import { BattleHud } from "./BattleHud";
import { BattleResult } from "./BattleResult";
import { QuestionPanel } from "./QuestionPanel";

interface BattleScreenProps {
  playerName?: string;
  questions?: readonly BattleQuestion[];
  configuration?: BattleConfiguration;
  subjectTitle?: string;
  reward?: BattleReward | null;
  rewardLoading?: boolean;
  onSubmitAnswer?: (
    questionId: string,
    questionIndex: number,
    selectedOptionIds: string[],
    responseTimeMs: number,
    useSpecial: boolean
  ) => Promise<ServerAnswerResolution>;
  onBattleComplete?: (result: {
    botHp: number;
    phase: "won" | "lost";
    playerHp: number;
  }) => void;
  onLeave?: () => void;
  onAbandonBattle?: () => Promise<void>;
  opponent?: BattleOpponentVisual;
}

const DEFAULT_OPPONENT: BattleOpponentVisual = {
  attackStyle: "melee",
  isBoss: false,
  name: "Ziyo",
  tier: 1,
  variant: 3
};

const RESULT_REVEAL_DELAY_MS = 2000;

export function BattleScreen({
  playerName,
  questions,
  configuration,
  subjectTitle,
  reward,
  rewardLoading,
  onSubmitAnswer,
  onBattleComplete,
  onLeave,
  onAbandonBattle,
  opponent = DEFAULT_OPPONENT
}: BattleScreenProps = {}) {
  const { t } = useTranslation();
  const resolvedPlayerName = playerName || t("playerFallbackName");
  const opponentName = opponent.name || t("opponentName");
  const playerHp = useBattleStore((state) => state.playerHp);
  const botHp = useBattleStore((state) => state.botHp);
  const playerMaxHp = useBattleStore((state) => state.playerMaxHp);
  const botMaxHp = useBattleStore((state) => state.botMaxHp);
  const secondsLeft = useBattleStore((state) => state.secondsLeft);
  const question = useBattleStore((state) => state.question);
  const selectedAnswers = useBattleStore((state) => state.selectedAnswers);
  const questionIndex = useBattleStore((state) => state.questionIndex);
  const combo = useBattleStore((state) => state.combo);
  const specialMeter = useBattleStore((state) => state.specialMeter);
  const feedback = useBattleStore((state) => state.feedback);
  const locked = useBattleStore((state) => state.locked);
  const phase = useBattleStore((state) => state.phase);
  const impact = useBattleStore((state) => state.impact);
  const answer = useBattleStore((state) => state.answer);
  const applyServerAnswer = useBattleStore((state) => state.applyServerAnswer);
  const configure = useBattleStore((state) => state.configure);
  const tick = useBattleStore((state) => state.tick);
  const clearFeedback = useBattleStore((state) => state.clearFeedback);
  const reset = useBattleStore((state) => state.reset);
  const reportedPhase = useRef<"won" | "lost" | null>(null);
  const questionStartedAt = useRef(performance.now());
  const pendingAnswer = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [resultVisible, setResultVisible] = useState(false);

  useBattleAudio({
    feedback,
    impact,
    phase,
    opponentAttackStyle: opponent.attackStyle
  });

  useEffect(() => {
    if (questions?.length) {
      configure(questions, configuration);
    }
  }, [configuration, configure, questions]);

  useEffect(() => {
    questionStartedAt.current = performance.now();
    setSubmitFailed(false);
  }, [question.id]);

  useEffect(() => {
    if (exitDialogOpen || phase !== "playing") {
      return;
    }
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, [exitDialogOpen, phase, tick]);

  useEffect(() => {
    if (!locked) {
      return;
    }

    const feedbackId = window.setTimeout(clearFeedback, 900);
    return () => window.clearTimeout(feedbackId);
  }, [clearFeedback, locked]);

  useEffect(() => {
    if (phase === "playing") {
      reportedPhase.current = null;
      setResultVisible(false);
      return;
    }

    if (reportedPhase.current !== phase) {
      reportedPhase.current = phase;
      onBattleComplete?.({ botHp, phase, playerHp });
    }
  }, [botHp, onBattleComplete, phase, playerHp]);

  useEffect(() => {
    if (phase === "playing") {
      return;
    }

    const revealTimer = window.setTimeout(
      () => setResultVisible(true),
      RESULT_REVEAL_DELAY_MS
    );
    return () => window.clearTimeout(revealTimer);
  }, [phase]);

  const liveMessage = feedback === "correct"
    ? t("correct", { damage: impact.amount, opponentName })
    : feedback === "incorrect"
      ? t("incorrect", { damage: impact.amount, playerName: resolvedPlayerName })
      : submitFailed
        ? t("battleAnswerError")
        : "";

  const handleAnswer = useCallback(async (
    values: readonly AnswerValue[],
    useSpecial: boolean
  ) => {
    if (!onSubmitAnswer) {
      answer(values, useSpecial);
      return;
    }

    if (pendingAnswer.current) {
      return;
    }

    pendingAnswer.current = true;
    setSubmitting(true);
    setSubmitFailed(false);

    try {
      const resolution = await onSubmitAnswer(
        question.id,
        questionIndex,
        [...values],
        Math.max(0, Math.round(performance.now() - questionStartedAt.current)),
        useSpecial
      );
      applyServerAnswer(values, resolution);
    } catch {
      setSubmitFailed(true);
    } finally {
      pendingAnswer.current = false;
      setSubmitting(false);
    }
  }, [answer, applyServerAnswer, onSubmitAnswer, question.id, questionIndex]);

  const restart = () => {
    audioEngine.playClick();
    if (onSubmitAnswer && onLeave) {
      onLeave();
      return;
    }
    reset();
  };

  return (
    <main className="battle-shell">
      <BattleHud
        playerHp={playerHp}
        botHp={botHp}
        playerMaxHp={playerMaxHp}
        botMaxHp={botMaxHp}
        playerName={resolvedPlayerName}
        botName={opponentName}
        secondsLeft={secondsLeft}
        opponent={opponent}
      />
      <section className="arena" aria-label={t("battleArena")}>
        {phase === "playing" && onAbandonBattle ? (
          <BattleExitDialog
            onConfirm={onAbandonBattle}
            onOpenChange={setExitDialogOpen}
          />
        ) : null}
        <AudioControls />
        <ArenaBackdrop />
        <BattleActors
          eventId={impact.eventId}
          impactTarget={impact.target}
          locked={locked}
          phase={phase}
          impactKind={impact.kind}
          opponent={opponent}
        />
        {locked && feedback !== "idle" && impact.eventId > 0 ? (
          <div
            className={`damage-callout damage-callout--${impact.kind} damage-callout--${impact.target}`}
            key={impact.eventId}
            aria-hidden="true"
          >
            {impact.kind === "critical" ? <strong>CRIT!</strong> : null}
            {impact.kind === "special" ? <strong>SPECIAL!</strong> : null}
            <span>−{impact.amount}</span>
          </div>
        ) : null}
      </section>
      <QuestionPanel
        key={question.id}
        question={question}
        selectedAnswers={selectedAnswers}
        feedback={feedback}
        locked={locked || submitting || phase !== "playing"}
        secondsLeft={secondsLeft}
        subjectTitle={subjectTitle}
        combo={combo}
        specialMeter={specialMeter}
        onAnswer={(values, useSpecial) => void handleAnswer(values, useSpecial)}
      />
      {resultVisible ? (
        <BattleResult
          phase={phase}
          playerName={resolvedPlayerName}
          onRestart={restart}
          onLeave={onLeave}
          loading={rewardLoading}
          reward={reward}
        />
      ) : null}
      <p className="visually-hidden" aria-live="polite">{liveMessage}</p>
    </main>
  );
}
